import React from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { DATE_FILTER_LABELS } from 'utils/dateUtils'

import { RISK_LEVELS_VALUES } from 'containers/SpearPhishingAgent/constants'
import {
  selectMetricBannerData,
  selectSpearPhishingAgentInfo,
} from 'containers/SpearPhishingAgent/selectors'

import Badge from 'components/lib/DesignSystem/V3/Badge'

import RiskExposureRadar from './RiskExposureRadar'

const RISK_LEVELS = [
  { key: RISK_LEVELS_VALUES.HIGH, label: 'High', color: 'var(--risk-high-color)' },
  {
    key: RISK_LEVELS_VALUES.MEDIUM,
    label: 'Medium',
    color: 'var(--osint-exposure-risk-medium-dot)',
  },
  { key: RISK_LEVELS_VALUES.LOW, label: 'Low', color: 'var(--osint-exposure-risk-low-dot)' },
]

const DAY_IN_MS = 24 * 60 * 60 * 1000
const getNextScanLabel = (nextScanInDays) => {
  if (nextScanInDays === null) {
    return 'Next scan date unavailable'
  }

  if (nextScanInDays === 0) {
    return 'Next scan due today'
  }

  return `Next scan in ${nextScanInDays} ${nextScanInDays === 1 ? 'day' : 'days'}`
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Accent = styled.div`
  width: 5px;
  height: 20px;
  border-radius: var(--t-borders-radius-rounded-lg);
  background: var(--t-colors-yellow-400);
`

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  flex: 1 1 auto;
  gap: 10px;
`

const Title = styled.div`
  color: #ffffff;
  font-size: var(--t-typography-font-size-base);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: var(--t-typography-line-height-base);
`

const PeriodTag = styled(Badge)`
  flex-shrink: 0;
`

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
  flex: 1 1 auto;
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  flex: 1 1 auto;
`

const TotalValue = styled.div`
  color: var(--t-colors-base-white);
  font-size: var(--t-typography-font-size-4xl);
  font-weight: var(--t-typography-font-weight-regular);
  line-height: 1;
`

const QuietTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const QuietTitle = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-family: var(--t-typography-font-family-font-family-default);
  font-size: 36px;
  font-weight: var(--t-typography-font-weight-regular);
  line-height: 40px;
`

const QuietMoonWrap = styled.div`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`

const QuietMoonSvg = styled.svg`
  width: 52px;
  height: 52px;
  display: block;
  overflow: visible;
`

const QuietCopy = styled.div`
  color: #ffffff;
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: 16px;
`

const QuietLine = styled.div`
  white-space: nowrap;
`

const QuietEmphasis = styled.span`
  color: #ffffff;
`

const QuietMuted = styled.span`
  color: rgba(255, 255, 255, 0.6);
`

const RiskRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const RiskBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: var(--t-borders-radius-rounded-lg);
  background: rgba(21, 27, 86, 0.2);
`

const RiskDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${(props) => props.$color};
`

const RiskValue = styled.div`
  font-family: var(--t-typography-font-family-font-family-default);
  color: #ffffff;
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: var(--t-typography-line-height-xs);
`

const RiskLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
`

const ExternalHighRiskExposurePlaceholder = () => {
  const metricBannerData = useSelector(selectMetricBannerData)
  const scheduleInfo = useSelector(selectSpearPhishingAgentInfo)
  const externalExposureByLevel = metricBannerData.data?.cardsExternalExposure?.data || {}
  const externalExposureLevels = Object.values(externalExposureByLevel)
  const externalExposureTotal = externalExposureLevels.reduce(
    (acc, level) => acc + Number(level?.total || 0),
    0
  )
  const nextScanDate = new Date(scheduleInfo?.data?.nextSchedule)
  const nextScanInDays = Number.isNaN(nextScanDate.getTime())
    ? null
    : Math.max(0, Math.ceil((nextScanDate.getTime() - Date.now()) / DAY_IN_MS))
  const visibleRiskLevels = RISK_LEVELS.map(({ key, label, color }) => ({
    key,
    label,
    color,
    value: Number(externalExposureByLevel[key]?.total || 0),
  })).filter(({ value }) => value > 0)
  const isQuietState = externalExposureTotal === 0

  return (
    <Container data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_Placeholder">
      <Header data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_Header_div">
        <Accent data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_Accent_div" />
        <HeaderMain>
          <Title data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_Title_div">
            Risk Exposure
          </Title>
          <PeriodTag
            dataTestId="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_Period_div"
            color="rgba(255, 255, 255, 0.08)"
            size="small"
            textColor="var(--t-colors-gray-400)"
            variant="filled"
          >
            {DATE_FILTER_LABELS.WEEK}
          </PeriodTag>
        </HeaderMain>
      </Header>

      <ContentRow data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_Content_div">
        <LeftColumn>
          {isQuietState ? (
            <>
              <QuietTitleRow data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_QuietTitle_div">
                <QuietTitle>All quiet</QuietTitle>
                <QuietMoonWrap>
                  <QuietMoonSvg
                    width="77"
                    height="77"
                    viewBox="0 0 77 77"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <g filter="url(#quietMoon_filter0_f_2010_6122)">
                      <path
                        d="M16 38.4564C16 26.0541 26.0541 16 38.4564 16C50.8588 16 60.9129 26.0541 60.9129 38.4564C60.9129 50.8588 50.8588 60.9129 38.4564 60.9129C26.0541 60.9129 16 50.8588 16 38.4564Z"
                        fill="white"
                        fillOpacity="0.1"
                      />
                    </g>
                    <g
                      opacity="0.6"
                      filter="url(#quietMoon_filter1_f_2010_6122)"
                      style={{ mixBlendMode: 'color-dodge' }}
                    >
                      <path
                        d="M31.6638 57.6982C31.6638 57.6982 36.3665 41.7123 34.1908 39.4799C31.6883 36.9123 16.1939 39.986 16.1939 39.986C18.5324 50.1424 23.0294 54.1558 31.6638 57.6982Z"
                        fill="white"
                        fillOpacity="0.1"
                      />
                    </g>
                    <path
                      d="M19.1209 45.3525C18.9636 45.0042 18.469 45.0042 18.3117 45.3525C18.2672 45.451 18.1883 45.5299 18.0898 45.5744C17.7414 45.7317 17.7414 46.2263 18.0898 46.3837C18.1883 46.4281 18.2672 46.507 18.3117 46.6055C18.469 46.9539 18.9636 46.9539 19.1209 46.6055C19.1654 46.507 19.2443 46.4281 19.3428 46.3837C19.6912 46.2263 19.6912 45.7317 19.3428 45.5744C19.2443 45.5299 19.1654 45.451 19.1209 45.3525Z"
                      fill="white"
                    />
                    <path
                      d="M20.2475 41.0715C20.1688 40.8973 19.9215 40.8973 19.8428 41.0715C19.8206 41.1208 19.7811 41.1602 19.7319 41.1824C19.5577 41.2611 19.5577 41.5084 19.7319 41.5871C19.7811 41.6093 19.8206 41.6488 19.8428 41.698C19.9215 41.8722 20.1688 41.8722 20.2475 41.698C20.2697 41.6488 20.3092 41.6093 20.3584 41.5871C20.5326 41.5084 20.5326 41.2611 20.3584 41.1824C20.3092 41.1602 20.2697 41.1208 20.2475 41.0715Z"
                      fill="white"
                    />
                    <path
                      d="M23.0129 49.4672C22.895 49.7285 22.524 49.7285 22.406 49.4672C22.3726 49.3933 22.3134 49.3342 22.2396 49.3008C21.9783 49.1828 21.9783 48.8118 22.2396 48.6938C22.3134 48.6605 22.3726 48.6013 22.406 48.5274C22.524 48.2662 22.895 48.2662 23.0129 48.5274C23.0463 48.6013 23.1055 48.6605 23.1794 48.6938C23.4406 48.8118 23.4406 49.1828 23.1794 49.3008C23.1055 49.3342 23.0463 49.3933 23.0129 49.4672Z"
                      fill="white"
                    />
                    <g
                      opacity="0.6"
                      filter="url(#quietMoon_filter2_f_2010_6122)"
                      style={{ mixBlendMode: 'color-dodge' }}
                    >
                      <path
                        d="M42.7414 13.4565L37.1938 34.7711L60.4115 31.2187C57.7405 21.0337 52.6038 17.009 42.7414 13.4565Z"
                        fill="white"
                        fillOpacity="0.1"
                      />
                    </g>
                    <path
                      d="M51.709 21.7561C52.0629 20.9724 53.1759 20.9724 53.5299 21.7561C53.63 21.9777 53.8075 22.1552 54.0291 22.2553C54.8129 22.6093 54.8129 23.7223 54.0291 24.0762C53.8075 24.1764 53.63 24.3539 53.5299 24.5755C53.1759 25.3592 52.0629 25.3592 51.709 24.5755C51.6089 24.3539 51.4313 24.1764 51.2097 24.0762C50.426 23.7223 50.426 22.6093 51.2097 22.2553C51.4313 22.1552 51.6089 21.9777 51.709 21.7561Z"
                      fill="white"
                    />
                    <path
                      d="M56.0322 29.1285C56.1502 28.8672 56.5212 28.8672 56.6392 29.1285C56.6726 29.2024 56.7317 29.2615 56.8056 29.2949C57.0668 29.4129 57.0668 29.7839 56.8056 29.9019C56.7317 29.9352 56.6726 29.9944 56.6392 30.0683C56.5212 30.3295 56.1502 30.3295 56.0322 30.0683C55.9988 29.9944 55.9397 29.9352 55.8658 29.9019C55.6046 29.7839 55.6046 29.4129 55.8658 29.2949C55.9397 29.2615 55.9988 29.2024 56.0322 29.1285Z"
                      fill="white"
                    />
                    <path
                      d="M48.7528 18.9045C48.8314 18.7303 49.0787 18.7303 49.1574 18.9045C49.1797 18.9538 49.2191 18.9932 49.2684 19.0155C49.4425 19.0941 49.4425 19.3414 49.2684 19.4201C49.2191 19.4423 49.1797 19.4818 49.1574 19.531C49.0787 19.7052 48.8314 19.7052 48.7528 19.531C48.7305 19.4818 48.6911 19.4423 48.6418 19.4201C48.4677 19.3414 48.4677 19.0941 48.6418 19.0155C48.6911 18.9932 48.7305 18.9538 48.7528 18.9045Z"
                      fill="white"
                    />
                    <path
                      d="M45.1574 21.9045C45.0787 21.7303 44.8314 21.7303 44.7528 21.9045C44.7305 21.9538 44.6911 21.9932 44.6418 22.0155C44.4676 22.0941 44.4676 22.3414 44.6418 22.4201C44.6911 22.4423 44.7305 22.4818 44.7528 22.531C44.8314 22.7052 45.0787 22.7052 45.1574 22.531C45.1796 22.4818 45.2191 22.4423 45.2683 22.4201C45.4425 22.3414 45.4425 22.0941 45.2683 22.0155C45.2191 21.9932 45.1796 21.9538 45.1574 21.9045Z"
                      fill="white"
                    />
                    <path
                      d="M50.4884 41.7651C50.3017 41.4501 49.7767 40.9601 48.47 41.1934C47.7467 41.3218 47.0117 41.3801 46.2767 41.3451C43.5584 41.2284 41.0967 39.9801 39.3817 38.0551C37.865 36.3634 36.9317 34.1584 36.92 31.7784C36.92 30.4484 37.1767 29.1651 37.7017 27.9518C38.215 26.7734 37.8534 26.1551 37.5967 25.8984C37.3284 25.6301 36.6984 25.2568 35.4617 25.7701C30.69 27.7768 27.7384 32.5601 28.0884 37.6818C28.4384 42.5001 31.8217 46.6184 36.3017 48.1701C37.375 48.5434 38.5067 48.7651 39.6734 48.8118C39.86 48.8234 40.0467 48.8351 40.2334 48.8351C44.1417 48.8351 47.805 46.9918 50.115 43.8534C50.8967 42.7684 50.6867 42.0801 50.4884 41.7651Z"
                      fill="#FFDD46"
                    />
                    <defs>
                      <filter
                        id="quietMoon_filter0_f_2010_6122"
                        x="0"
                        y="0"
                        width="76.9128"
                        height="76.9131"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="BackgroundImageFix"
                          result="shape"
                        />
                        <feGaussianBlur
                          stdDeviation="8"
                          result="effect1_foregroundBlur_2010_6122"
                        />
                      </filter>
                      <filter
                        id="quietMoon_filter1_f_2010_6122"
                        x="4.19385"
                        y="26.4565"
                        width="42.5718"
                        height="43.2417"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="BackgroundImageFix"
                          result="shape"
                        />
                        <feGaussianBlur
                          stdDeviation="6"
                          result="effect1_foregroundBlur_2010_6122"
                        />
                      </filter>
                      <filter
                        id="quietMoon_filter2_f_2010_6122"
                        x="25.1938"
                        y="1.45654"
                        width="47.2178"
                        height="45.3145"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="BackgroundImageFix"
                          result="shape"
                        />
                        <feGaussianBlur
                          stdDeviation="6"
                          result="effect1_foregroundBlur_2010_6122"
                        />
                      </filter>
                    </defs>
                  </QuietMoonSvg>
                </QuietMoonWrap>
              </QuietTitleRow>
              <QuietCopy>
                <QuietLine data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_QuietSubtitle_div">
                  <QuietEmphasis>No new OSINT exposure found.</QuietEmphasis>
                </QuietLine>
                <QuietLine data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_QuietNextScan_div">
                  <QuietMuted>{getNextScanLabel(nextScanInDays)}</QuietMuted>
                </QuietLine>
              </QuietCopy>
            </>
          ) : (
            <>
              <TotalValue data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_Total_div">
                {externalExposureTotal}
              </TotalValue>
              <RiskRow data-testid="Psima_Dashboard_TopBanner_ExternalHighRiskExposure_Risks_div">
                {visibleRiskLevels.map(({ key, label, color, value }) => (
                  <RiskBadge
                    key={key}
                    data-testid={`Psima_Dashboard_TopBanner_ExternalHighRiskExposure_Risk_${key}_div`}
                  >
                    <RiskDot $color={color} />
                    <RiskValue>
                      {value} <RiskLabel>{label}</RiskLabel>
                    </RiskValue>
                  </RiskBadge>
                ))}
              </RiskRow>
            </>
          )}
        </LeftColumn>

        <RiskExposureRadar
          showDataPoints={!isQuietState}
          testIdPrefix="Psima_Dashboard_TopBanner_ExternalHighRiskExposure"
        />
      </ContentRow>
    </Container>
  )
}

export default ExternalHighRiskExposurePlaceholder
